import React, { useState } from 'react';
import { CheckIcon, ChevronDownIcon } from 'lucide-react';
import { SectionHeading } from './SectionHeading';
import { volumes } from '../data/volumes';
import { volumeChecks } from '../data/copy';
/** Topics shown per volume before the "+ N more" line. */
const VISIBLE_TOPICS = 6;
export function VolumesSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  return <section id="volumes" className="w-full scroll-mt-6 bg-slate-950 py-10 [contain-intrinsic-size:auto_900px] [content-visibility:auto] sm:py-14 [.chalk_&]:bg-slate-50" aria-labelledby="volumes-heading" data-id="element-241" data-mp="3g4g7ta1ql3ry">
      <div className="mx-auto max-w-4xl px-6" data-id="element-242" data-mp="3pvaxkoe6yli5">
        <SectionHeading eyebrow="The complete system" title={<span id="volumes-heading" data-id="element-244" data-mp="3l39mh5srf0tp">
              A visual coaching system built for ages 6–12
            </span>} data-id="element-243" data-mp="3px3zjqdv0hjt" />

        <div className="mx-auto mt-6 max-w-2xl" data-id="element-245" data-mp="2xd5igxbnsz26">
          <p className="text-center font-semibold text-slate-50 [.chalk_&]:text-slate-900" data-id="element-246" data-mp="2sfwxyq31gixr">
            Open any volume and quickly see:
          </p>
          <ul className="mt-4 grid grid-cols-1 gap-x-6 gap-y-2 min-[420px]:grid-cols-2" data-id="element-247" data-mp="29ez49y5ngk1a">
            {volumeChecks.map(item => <li key={item} className="flex items-start gap-2 text-[0.95rem] text-slate-400 [.chalk_&]:text-slate-600" data-id="element-248" data-mp="2eciiiy2ckwt8">
                <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-orange-400 [.chalk_&]:text-orange-600" strokeWidth={2.6} aria-hidden="true" data-id="element-249" data-mp="2o4esp7hpkddt" />
                {item}
              </li>)}
          </ul>
        </div>

        <ol className="mt-10 space-y-3" data-id="element-250" data-mp="1vfhfhisct6rx">
          {volumes.map((volume, index) => {
          const isOpen = openIndex === index;
          const topics = volume.highlights.slice(0, VISIBLE_TOPICS);
          const moreCount = volume.highlights.length - topics.length;
          return <li key={volume.code} className="relative overflow-hidden rounded-3xl border transition-colors duration-150 ease-out" style={{
            borderColor: isOpen ? volume.color : `${volume.color}33`,
            backgroundColor: `${volume.color}0a`
          }} data-id="element-251" data-mp="24e2tr1qslzmw" data-mpi={index}>
                <span className="absolute inset-y-0 left-0 w-1.5" style={{
              backgroundColor: volume.color
            }} aria-hidden="true" data-id="element-252" data-mp="1gozqphaelhrg" />
                <h3 data-id="element-253" data-mp="1prr43mmuanlq">
                  <button type="button" onClick={() => setOpenIndex(isOpen ? null : index)} aria-expanded={isOpen} aria-controls={`volume-panel-${index}`} className="flex w-full items-center gap-4 rounded-3xl py-4 pl-6 pr-5 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400 sm:gap-5 sm:pl-8 sm:pr-6 [.chalk_&]:focus-visible:outline-orange-600" data-id="element-254" data-mp="1pq7o7x53iekh">
                    <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-xl font-extrabold text-slate-950" style={{
                  backgroundColor: volume.color
                }} aria-hidden="true" data-id="element-255" data-mp="16ty0ei4e69na">
                      {volume.code}
                    </span>
                    <span className="min-w-0 flex-1" data-id="element-256" data-mp="9czd9e5cdoej">
                      <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-slate-400 [.chalk_&]:text-slate-600" data-id="element-257" data-mp="2l0cis4njya7t">
                        Volume {volume.code} · {volume.count}
                      </span>
                      <span className="mt-0.5 block text-lg font-bold uppercase leading-tight tracking-tight text-slate-50 sm:text-xl [.chalk_&]:text-slate-900" data-id="element-258" data-mp="2c1hfp1ft1g8q">
                        {volume.name}
                      </span>
                    </span>
                    <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-900 transition-transform duration-200 ease-out [.chalk_&]:bg-white ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-259" data-mp="38qp189gfbpcn">
                      <ChevronDownIcon className="h-4 w-4 text-slate-400 [.chalk_&]:text-slate-600" data-id="element-260" data-mp="2zo7e7ssy2sol" />
                    </span>
                  </button>
                </h3>

                <div id={`volume-panel-${index}`} className={`grid transition-[grid-template-rows,opacity] duration-200 ease-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`} data-id="element-261" data-mp="1oav7na5svvyq">
                  <div className="overflow-hidden" data-id="element-262" data-mp="1yt0bwybs9obw">
                    <div className="pb-5 pl-6 pr-5 sm:pl-8 sm:pr-6" data-id="element-263" data-mp="1yr5rrm8c608v">
                      <p className="text-[0.98rem] font-medium leading-snug text-slate-50 [.chalk_&]:text-slate-900" data-id="element-264" data-mp="1usgdlrfh4eo6">{volume.intro}</p>

                      <div className="mt-3.5 grid gap-2.5 sm:grid-cols-[1.35fr_1fr]" data-id="element-265" data-mp="1pqr4ihbtab2q">
                        <div className="rounded-2xl px-4 py-3.5" style={{
                      backgroundColor: `${volume.color}14`
                    }} data-id="element-266" data-mp="2mgqm119xwjxg">
                          <p className="text-xs font-bold uppercase tracking-[0.18em]" style={{
                        color: volume.color
                      }} data-id="element-267" data-mp="2dh3l0gl4hg4x">
                            Inside
                          </p>
                          <ul className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1.5" data-id="element-268" data-mp="124awrfrjj9tm">
                            {topics.map(item => <li key={item} className="flex items-start gap-1.5 text-[0.82rem] font-medium leading-snug text-slate-50 [.chalk_&]:text-slate-900" data-id="element-269" data-mp="x2lkjwsl0q3y">
                                <span className="mt-[0.4rem] h-1.5 w-1.5 shrink-0 rounded-full" style={{
                            backgroundColor: volume.color
                          }} aria-hidden="true" data-id="element-270" data-mp="s046i8v71xuz" />
                                {item}
                              </li>)}
                          </ul>
                          {moreCount > 0 ? <p className="mt-2 text-[0.78rem] font-semibold text-slate-400 [.chalk_&]:text-slate-600" data-id="element-271" data-mp="15q7kydnu0n9v">
                              + {moreCount} more topics
                            </p> : null}
                        </div>

                        <div className="flex flex-col rounded-2xl bg-slate-900 px-4 py-3.5 ring-1 ring-slate-800 [.chalk_&]:bg-white [.chalk_&]:ring-slate-200" data-id="element-272" data-mp="1ko9ud4hjo2z1">
                          <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.18em] text-orange-400 [.chalk_&]:text-orange-600" data-id="element-273" data-mp="3hi39bwdgc9nb">
                            <CheckIcon className="h-3.5 w-3.5" strokeWidth={3} aria-hidden="true" data-id="element-274" data-mp="3efgjdxvx88ur" />
                            Result
                          </p>
                          <p className="mt-2 text-[0.85rem] font-medium leading-snug text-slate-50 [.chalk_&]:text-slate-900" data-id="element-275" data-mp="3ocl6bzxlc8cm">
                            {volume.result}
                          </p>
                          {volume.note ? <p className="mt-auto pt-3 text-[0.78rem] font-semibold" style={{
                        color: volume.color
                      }} data-id="element-276" data-mp="17dg2bvrudfn">
                              {volume.note}
                            </p> : null}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </li>;
        })}
        </ol>
      </div>
    </section>;
}